import ListaAcoesRapidas, { TItemAcaoRapida } from "../../../../../components/ListaAcoesRapidas";
import ScreenBanner from "../../../../../components/ScreenBanner";
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Container, Grid, IconButton, Paper, Stack, Typography } from "@mui/material";
import { confirm } from "material-ui-confirm";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSetParamsURL from "../../../../../lib/useSetParamsURL";
import BtnExportar from "../../../../../components/BtnExportar";
import { TableDataGridRef, TableDataGridV2 } from "../../../../../components/TableDataGridV2";
import { processFilterValues } from "../../../../../components/TableDataGridV2/helpers";
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import PreFiltros from "../PreFiltrosListagem";
import { enqueueSnackbar } from "notistack";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../lib/axios";
import DialogIncluirPrescricao from "../DialogIncluirPrescricao";
import { IDetalharPrescricao } from "../../../../../types/api/receituarioAgronomico/detalhar-prescricoes";
import { colunasPrescricao } from "../dataGridConfig";
import { useModalDetalhePrescricao } from "../ModalDetalhePrescricaoContext";
import DeleteIcon from "@mui/icons-material/Delete";
import PermissionGuard, { usePermission } from "../../../../../components/PermissionGuard";

export default function Listagem() {
    const tableRef = useRef<TableDataGridRef>(null);
    const { openModal: openPrescricaoDetailModal } = useModalDetalhePrescricao();
    const { setParamsUrl, searchParams, setMultipleParamsUrl } = useSetParamsURL();

    const [preFilter, setPreFilter] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const queryClient = useQueryClient();

    const handleChangePreFilters = useCallback((value: TPreFilters) => {
        const filteredValues = processFilterValues(value, (key) => {
            if (!filteredValues) return;
            filteredValues[key] = "";
        });

        setMultipleParamsUrl(filteredValues);
        if (containerRef.current) {
            containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [setMultipleParamsUrl]);

    const handleClosePreFilter = useCallback(() => {
        setPreFilter(false);
    }, []);

    const [openModalIncluir, setOpenModalIncluir] = useState<TOpenModalPrescricoes | null>(null);

    const listaAcoesRapidas: Array<TItemAcaoRapida> = [
        {
            label: `Incluir prescrição`,
            icon: <AddIcon color="primary" />,
            action: () => handleModalAction("open", null),
            havePermission: usePermission("receituario.incluir-prescricao"),
        }
    ];

    const handleModalAction = useCallback(
        (action: "open" | "close", value?: IDetalharPrescricao | null) => {
            if (action === "close") {
                setOpenModalIncluir(null);
            } else if (action === "open") {
                setOpenModalIncluir({ open: true, defaultValue: value || null });
            }
        },
        []
    );

    const { mutate: excluirPrescricao } = useMutation({
        mutationKey: "excluir-prescricao",
        mutationFn: (ids: number[]) =>
            axiosClient.delete(`/receituario-agronomico/prescricoes/excluir-prescricao`, { data: { ids } }),
        onError: () => {
            enqueueSnackbar("Erro ao excluir prescrição!", { variant: "error" });
        },
        onSuccess: () => {
            queryClient.invalidateQueries("listar-todas-prescricoes");
            enqueueSnackbar("Prescrições excluídas com sucesso!", {
                variant: "success",
            });
        },
    });

    const handleDeleteSelected = useCallback(() => {
        if (tableRef.current) {
            const selectedRows: IDetalharPrescricao[] = tableRef.current.getSelectedRows();
            if (selectedRows.length === 0) {
                enqueueSnackbar("Por favor, selecione pelo menos uma prescrição.", {
                    variant: "warning",
                });
                return;
            }
            const ids = selectedRows.map(row => row.id).filter((id): id is number => id !== undefined);
            confirm({ description: `Tem certeza que deseja excluir ${ids.length > 1 ? 'as prescrições selecionadas' : 'a prescrição selecionada'}?` })
                .then(() => excluirPrescricao(ids))
                .catch(() => { /* User cancelled confirmation */ });
        }
    }, [excluirPrescricao]);


    const RenderListagem = useMemo(() => {
        const TableComponent = () => (
            <>
                {
                    preFilter &&
                    <Box
                        minHeight={250}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <PreFiltros
                            filtros={searchParams}
                            onSubmitFilters={(values) => {
                                handleChangePreFilters(values);
                                handleClosePreFilter();
                            }}
                        />
                    </Box>
                }
                {
                    !preFilter &&
                    <>
                        <Box sx={{ display: "flex", alignItems: "flex-end", p: 2 }}>
                            <BtnExportar boxProps={{ p: 0 }} />
                            <Button sx={{ mx: 1 }} onClick={handleDeleteSelected} variant="outlined" size="small" startIcon={<DeleteIcon />}>
                                Excluir
                            </Button>
                        </Box>
                        <TableDataGridV2
                            enableSelection={true}
                            urlParams
                            ref={tableRef}
                            columns={colunasPrescricao}
                            url="/receituario-agronomico/prescricoes/pesquisar-prescricao"
                            queryKey="listar-todas-prescricoes"
                            pagination="server"
                            queryParams={{ is_listagem: "true" }}
                            onRowDoubleClick={(row) => {
                                const id = typeof row === 'object' ? (row as IDetalharPrescricao).id : row;
                                if (id) {
                                    setParamsUrl(id.toString(), "detalhe-prescricao");
                                    openPrescricaoDetailModal(id.toString());
                                }
                            }}
                        />
                    </>
                }
            </>
        );

        return React.memo(TableComponent);
    }, [preFilter, searchParams, handleChangePreFilters, handleClosePreFilter, openPrescricaoDetailModal, setParamsUrl, handleDeleteSelected]);

    useEffect(() => {
        if (searchParams.size > 0) {
            setPreFilter(false)
        }
    }, [searchParams])

    return (
        <>
            <ScreenBanner title="Receituário Agronômico" />
            <PermissionGuard permissionId="receituario.listar-prescricoes">
                <Container sx={{ mt: 3 }} ref={containerRef}>
                    <Typography textAlign="center" variant="h5">Prescrições</Typography>
                    <Grid container spacing={2} mt={1}>
                        <Grid size={{xs: 12, md: 3}}>
                            <ListaAcoesRapidas
                                items={listaAcoesRapidas}
                                fullHeight={false}
                            />
                        </Grid>

                        <Grid size={{xs: 12, md: 9}}>
                            <Box component={Paper}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" px={3} pt={2} pb={preFilter ? 2 : 0}>
                                    <Typography variant="h6">Lista de prescrições</Typography>
                                    {
                                        !preFilter &&
                                        <Stack alignItems="flex-end" justifyContent="center" direction="row" gap={2}>
                                            <IconButton sx={{ p: 1 }} aria-label="filtros" onClick={() => setPreFilter(true)}>
                                                <TuneOutlinedIcon />
                                            </IconButton>
                                        </Stack>
                                    }
                                </Box>
                                <RenderListagem />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
                {
                    openModalIncluir?.open && (
                        <DialogIncluirPrescricao
                            defaultValue={openModalIncluir.defaultValue}
                            open={openModalIncluir.open}
                            handleClose={() => handleModalAction("close")}
                        />
                    )
                }
            </PermissionGuard>
        </>
    )
}

export type TPreFilters = {
    [key: string]: any;
};

type TOpenModalPrescricoes = {
    open: boolean;
    defaultValue?: IDetalharPrescricao | null;
};
